import React from "react";
import { Button, Card, CardContent, CardHeader, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup } from "@mui/material";
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import { Box } from "@mui/system";
import _ from 'lodash'
import PercentIcon from '@mui/icons-material/Percent';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import { ComposedChart, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import ChartTooltip from "../../../charts/ChartTooltip";
import ChartEmpty from "../../../charts/ChartEmpty";
import { DualAxisChart } from "../../../charts/CompositChart";
import { allZones, transformChartDataToTable } from "../../../../helpers/UtilityHelper";
import { ZONE_COLORS, ACCENT } from "../../../charts/colors";

export default function ZoneWiseSplit({ data, title }) {
    const [view, setView] = React.useState("chart")
    const [tableView, setTableView] = React.useState("percentage")
    const [showCount, setShowCount] = React.useState(true)



    const { rows: rowsOutTat, allColumns } = transformChartDataToTable(data, [
        ...allZones.map((z) => {
            const key = tableView === 'percentage' ? `c_${z}_perc_out` : `c_${z}_count_out`
            return { key, label: `${z}` }
        })
    ])

    const { rows: rowsInTat } = transformChartDataToTable(data, [
        ...allZones.map((z) => {
            const key = tableView === 'percentage' ? `c_${z}_perc_in` : `c_${z}_count_in`
            return { key, label: `${z}` }
        }),
    ])

    const fallbackChartOptions = {
        layout: 'horizontal',
        xaxis: 'name',
        yaxis: {
            left: {
                label: 'Out TAT Percentage',
                data: [
                    {
                        dataKey: 'totalOutTatPercentage',
                        label: 'Out-TAT %',
                        chartType: 'line',
                        fill: ACCENT.red,
                        unit: '%'
                    },
                ]
            }
        },
    }

    return (
        <Card elevation={1}>
            <CardHeader title={title} action={(
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button size="small" disableElevation onClick={() => setShowCount(st => !st)} variant={showCount ? "contained" : "outlined"} sx={{ mr: 1 }}>Show All Zones</Button>
                    <ToggleButtonGroup color="primary" size="small" exclusive onChange={(e, v) => setView(v)} value={view}>
                        <ToggleButton value="chart" title="View Chart">
                            <BarChartIcon />
                        </ToggleButton>
                        <ToggleButton value="table" title="View Table">
                            <TableChartIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    {(view === "table") && (
                        <ToggleButtonGroup sx={{ ml: 1 }} color="primary" size="small" exclusive onChange={(e, v) => setTableView(v)} value={tableView}>
                            <ToggleButton value="percentage" title="View Data In %">
                                <PercentIcon />
                            </ToggleButton>
                            <ToggleButton value="number" title="View Data In Count">
                                <LooksOneIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </Box>
            )} />
            <CardContent sx={{ px: view === 'chart' ? 1 : 0 }}>
                {view === 'chart' && !showCount && (
                    <DualAxisChart data={data} chartOptions={fallbackChartOptions} />
                )}
                {view === 'chart' && showCount && (
                    (!data || data.length === 0) ? <ChartEmpty height={440} /> : (
                    <ResponsiveContainer width="100%" height={440}>
                        <ComposedChart data={data} margin={{ top: 30, right: 20, bottom: 5, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#dee2e6" />
                            <XAxis dataKey="name" fontSize="11px" fontWeight="500" />
                            <YAxis yAxisId="left" domain={[0, 100]} fontSize="11px" fontWeight="500" label={{ value: '% Shipments(Out TAT)', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#666', textAnchor: 'middle' }} />
                            <YAxis yAxisId="right" orientation="right" fontSize="11px" fontWeight="500" label={{ value: '# Total Shipments(Out-TAT)', angle: -90, position: 'insideRight', fontSize: 11, fill: '#666', textAnchor: 'middle' }} />
                            <Tooltip content={<ChartTooltip />} />
                            <Legend verticalAlign="top" align="center" iconSize="10px" iconType="circle" wrapperStyle={{ fontSize: 11, fontWeight: 500, paddingBottom: 10 }} />
                            <Bar yAxisId="right" dataKey="totalOutTat" name="Total Shipments(Out-TAT)" fill="#E5E7EB" fillOpacity={0.55} maxBarSize={40}>
                                <LabelList dataKey="totalOutTat" position="top" style={{ fontSize: 10, fontWeight: 600, fill: '#374151' }} formatter={(v) => (v == null || v === 0) ? '' : v} />
                            </Bar>
                            {allZones.map((z) => {
                                const color = ZONE_COLORS[z] || '#999'
                                return (
                                    <Area
                                        key={z}
                                        yAxisId="left"
                                        dataKey={`c_${z}_perc_out`}
                                        name={`${z} Out-TAT`}
                                        stackId="zones"
                                        type="monotone"
                                        stroke={color}
                                        fill={color}
                                        fillOpacity={0.85}
                                        strokeWidth={1}
                                        animationEasing="ease-in-out"
                                        animationDuration={1000}
                                    >
                                        <LabelList
                                            dataKey={`c_${z}_perc_out`}
                                            content={({ x, y, value }) => {
                                                if (!value || value === 0 || x == null || y == null) return null
                                                const segPx = (value / 100) * 360
                                                if (segPx < 16) return null
                                                return (
                                                    <text
                                                        x={x}
                                                        y={y + segPx / 2 + 3}
                                                        fill="rgba(0,0,0,0.55)"
                                                        fontSize={9}
                                                        fontWeight={500}
                                                        textAnchor="middle"
                                                    >
                                                        {`${Number(value).toFixed(1)}%`}
                                                    </text>
                                                )
                                            }}
                                        />
                                    </Area>
                                )
                            })}
                        </ComposedChart>
                    </ResponsiveContainer>
                    )
                )}
                {view === 'table' && (
                   <TableContainer sx={{ maxHeight: 1200, overflowX: 'auto' }}>
                   <Table size="small" density sx={{ minWidth: 750, borderCollapse: 'collapse' }}>
                       <TableHead sx={{ backgroundColor: '#000', color: '#fff' }}>
                           <TableRow sx={{ borderBottom: '1px solid #ccc' }}>
                               {/* Header sticky cell */}
                               <TableCell sx={{
                                   borderRight: '1px solid #ccc',
                                   color: '#fff',
                                   position: 'sticky',
                                   left: 0,
                                   zIndex: 2,
                                   backgroundColor: '#000'  // Header background color
                               }} colSpan={1}>Name</TableCell>
                               {allColumns.map((val, j) => (
                                   <TableCell key={j} sx={{ borderRight: '1px solid #ccc', color: '#fff' }} colSpan={2}>{val}</TableCell>
                               ))}
                           </TableRow>
                           <TableRow>
                               {/* Placeholder for the sticky cell in the second header row */}
                               <TableCell sx={{
                                   position: 'sticky',
                                   left: 0,
                                   zIndex: 2,
                                   backgroundColor: '#000',  // Header background color
                                   borderRight: '1px solid #ccc'
                               }} />
                               {allColumns.map((_val, i) => (
                                   <React.Fragment key={i}>
                                       <TableCell sx={{ borderRight: '1px solid #ccc', color: '#fff' }}>IN</TableCell>
                                       <TableCell sx={{ borderRight: '1px solid #ccc', color: '#fff' }}>OUT</TableCell>
                                   </React.Fragment>
                               ))}
                           </TableRow>
                       </TableHead>
                       <TableBody>
                           {rowsOutTat.map((row, i) => {
                               const inTatData = _.find(rowsInTat, { name: row.name })
                               return (
                                   <TableRow key={i} sx={{ borderBottom: '1px solid #ccc' }}>
                                       {/* Body sticky cell */}
                                       <TableCell sx={{
                                           borderRight: '1px solid #ccc',
                                           position: 'sticky',
                                           left: 0,
                                           zIndex: 1,
                                           backgroundColor: '#fff',  // Body row background color
                                           // Add padding if needed, and make the text bold or adjust as per design
                                           padding: '6px',
                                       }}>{row.name}</TableCell>
                                       {allColumns.map((val, j) => (
                                           <React.Fragment key={j}>
                                               <TableCell sx={{ borderRight: '1px solid #ccc' }}>{_.get(inTatData, val, 0)}</TableCell>
                                               <TableCell sx={{ borderRight: '1px solid #ccc' }}>{_.get(row, val, 0)}</TableCell>
                                           </React.Fragment>
                                       ))}
                                   </TableRow>
                               );
                           })}
                       </TableBody>
                   </Table>
               </TableContainer>

                )}
            </CardContent>
        </Card>
    )
}
